import { memo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import type { DocumentReference } from "fhir/r5";
import {
  extractDocumentVerification,
} from "src-next/modules/patient-supporting-documents";
import {
  docDisplayTitle,
  docFileLabel,
  docMetaLine,
  resolveDocStatus,
} from "../../helpers/documentsChecklistHelpers";

export interface RejectTarget {
  doc: DocumentReference;
  categoryLabel: string;
  title: string;
}

const DOC_STATUS_META: Record<
  "pending" | "approved" | "rejected",
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  pending: { label: "Pendente", color: "info" },
  approved: { label: "Verificado", color: "success" },
  rejected: { label: "Recusado", color: "error" },
};

const compactBtnSx = {
  textTransform: "none" as const,
  minWidth: 0,
  px: 1,
  py: 0.375,
  fontSize: "0.72rem",
  lineHeight: 1.3,
  whiteSpace: "nowrap" as const,
};

interface DocumentTileProps {
  doc: DocumentReference;
  index: number;
  categoryLabel: string;
  organizationId?: string;
  canVerify: boolean;
  busyDocId: string | null;
  approvingAll: boolean;
  inherited?: boolean;
  onView: (doc: DocumentReference, title: string) => void;
  onApprove: (doc: DocumentReference, title: string) => void;
  onReject: (target: RejectTarget) => void;
  onRevokeApproval?: (doc: DocumentReference, title: string) => void;
  onRevokeRejection?: (doc: DocumentReference, title: string) => void;
}

export const DocumentTile = memo(function DocumentTile({
  doc,
  index,
  categoryLabel,
  organizationId,
  canVerify,
  busyDocId,
  approvingAll,
  inherited = false,
  onView,
  onApprove,
  onReject,
  onRevokeApproval,
  onRevokeRejection,
}: DocumentTileProps) {
  const id = doc.id as string;
  const title = docDisplayTitle(doc, index);
  const verification = extractDocumentVerification(doc, organizationId);
  const statusKey = resolveDocStatus(doc, organizationId);
  const docMeta = DOC_STATUS_META[statusKey];
  const metaLine = docMetaLine(doc, verification, statusKey);
  const fileLabel = docFileLabel(doc);
  const verified = statusKey === "approved";
  const rejected = statusKey === "rejected";
  const isBusy = busyDocId === id;
  const showActions = canVerify && !verified && !rejected && !inherited;
  const expired = Boolean(verification.isExpired);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "grey.200",
        borderRadius: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 132,
        minWidth: 220,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Visualizar ${title}`}
        onClick={() => onView(doc, `${categoryLabel} — ${title}`)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onView(doc, `${categoryLabel} — ${title}`);
          }
        }}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 72,
          px: 1.25,
          py: 1,
          cursor: "pointer",
          outline: "none",
          overflow: "hidden",
          "&:hover .doc-preview-overlay": {
            opacity: 1,
          },
          "&:hover .doc-preview-content": {
            opacity: 0,
          },
          "&:focus-visible": {
            boxShadow: (theme) =>
              `inset 0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box
          className="doc-preview-overlay"
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            bgcolor: "rgba(0, 0, 0, 0.52)",
            opacity: 0,
            transition: "opacity 0.18s ease",
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 32, color: "common.white" }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "common.white",
              letterSpacing: 0.3,
              textShadow: "0 1px 2px rgba(0,0,0,0.35)",
            }}
          >
            Verificar
          </Typography>
        </Box>

        <Box
          className="doc-preview-content"
          sx={{
            position: "relative",
            zIndex: 1,
            transition: "opacity 0.18s ease",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>
          {metaLine ? (
            <Typography
              variant="caption"
              color={
                rejected
                  ? "error.main"
                  : verified
                    ? "success.main"
                    : "text.secondary"
              }
              display="block"
              sx={{
                mt: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {metaLine}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
        sx={{
          flexShrink: 0,
          px: 1,
          py: 0.75,
          minHeight: 44,
          borderTop: 1,
          borderColor: "grey.100",
          bgcolor: "grey.50",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 0, maxWidth: "42%" }}
        >
          {fileLabel}
        </Typography>

        {showActions ? (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flex: 1, justifyContent: "flex-end", minWidth: 0 }}
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={Boolean(busyDocId) || approvingAll}
              onClick={() => onApprove(doc, title)}
              sx={{
                ...compactBtnSx,
                bgcolor: "success.main",
                boxShadow: 1,
                "&:hover": { bgcolor: "success.dark" },
              }}
            >
              {isBusy ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                "Aprovar"
              )}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={Boolean(busyDocId) || approvingAll}
              onClick={() => onReject({ doc, categoryLabel, title })}
              sx={{
                ...compactBtnSx,
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              Reprovar
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            onClick={(event) => event.stopPropagation()}
          >
            {inherited && verified ? (
              <Chip
                size="small"
                label="Herdado"
                color="success"
                variant="outlined"
                sx={{
                  height: 24,
                  "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" },
                }}
              />
            ) : (
              <Chip
                size="small"
                label={
                  expired && verified ? "Vencido" : docMeta.label
                }
                color={expired && verified ? "warning" : docMeta.color}
                variant="outlined"
                sx={{
                  height: 24,
                  flexShrink: 0,
                  "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" },
                }}
              />
            )}
            {canVerify && verified && onRevokeApproval && !inherited ? (
              <Button
                size="small"
                variant="text"
                disabled={Boolean(busyDocId)}
                onClick={() => onRevokeApproval(doc, title)}
                sx={compactBtnSx}
              >
                Desfazer
              </Button>
            ) : null}
            {canVerify && rejected && onRevokeRejection ? (
              <Button
                size="small"
                variant="text"
                color="warning"
                disabled={Boolean(busyDocId)}
                onClick={() => onRevokeRejection(doc, title)}
                sx={compactBtnSx}
              >
                Desfazer recusa
              </Button>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Box>
  );
});
